import { useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { Home, LineChart, Settings, ChefHat, Dumbbell } from "lucide-react";
import { useI18n } from "@/lib/i18n.jsx";

// Prefetch the sibling route bundles in the background so tapping a nav
// tab doesn't stall on the first ~200-500 ms of chunk download. Every
// entry here mirrors a `React.lazy(() => import(...))` in App.js — webpack
// dedupes by request string, so calling the same import() twice hits the
// same cached chunk.
//
// Home/Dashboard is NOT listed — it's synchronously imported in App.js so
// it's already in the main bundle. Only the four lazy siblings need warmup.
//
// Fires during requestIdleCallback (falls back to setTimeout on Safari)
// so it never competes with the initial Dashboard paint. Also re-fires on
// `pointerdown`/`touchstart` of a nav item — worst case we save one round
// trip; best case the tap is instant.
const ROUTE_PREFETCH = {
  progress: () => import(/* webpackPrefetch: true */ "@/pages/Progress"),
  workouts: () => import(/* webpackPrefetch: true */ "@/pages/WorkoutsPage"),
  recipes:  () => import(/* webpackPrefetch: true */ "@/pages/RecipesPage"),
  coach:    () => import(/* webpackPrefetch: true */ "@/pages/AICoach"),
  settings: () => import(/* webpackPrefetch: true */ "@/pages/Settings"),
};
const fired = new Set();
function prefetch(id) {
  if (fired.has(id)) return;
  fired.add(id);
  ROUTE_PREFETCH[id]?.().catch(() => { fired.delete(id); });
}
function prefetchAll() {
  Object.keys(ROUTE_PREFETCH).forEach(prefetch);
}

// Custom Coach icon (small orange robot mask)
function CoachIcon({ active }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="8" width="16" height="10" rx="4" />
      <circle cx="9" cy="13" r="1" fill={active ? "currentColor" : "none"} />
      <circle cx="15" cy="13" r="1" fill={active ? "currentColor" : "none"} />
      <path d="M12 3v3M8 18v2M16 18v2" />
    </svg>
  );
}

export default function BottomNav() {
  const { t, lang } = useI18n();
  const prefetchedRef = useRef(false);

  // Warm every sibling chunk during idle time — the user is likely to
  // tap at least one nav tab in the next few seconds, and by then all
  // five bundles will already be parsed.
  useEffect(() => {
    if (prefetchedRef.current) return;
    prefetchedRef.current = true;
    const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 800));
    const cancelRic = window.cancelIdleCallback || clearTimeout;
    const handle = ric(prefetchAll, { timeout: 3000 });
    return () => cancelRic(handle);
  }, []);

  // Order (visual right→left for RTL reading; browser flips natural order
  // when `dir="rtl"` is set on `<html>`, so first item ends up on the RIGHT
  // in Arabic and on the LEFT in English — both match user expectation).
  const items = [
    { to: "/app",          icon: Home,      labelKey: "nav.home",     id: "home",     end: true, isCustom: false },
    { to: "/app/progress", icon: LineChart, labelKey: "nav.progress", id: "progress",            isCustom: false },
    { to: "/app/recipes",  icon: ChefHat,   labelKey: "nav.recipes",  id: "recipes",             isCustom: false },
    // Arabic-only for now (owner request) — sits between recipes and the coach.
    ...(lang === "en" ? [] : [{ to: "/app/workouts", icon: Dumbbell, labelKey: "nav.workouts", id: "workouts", isCustom: false }]),
    { to: "/app/coach",    icon: CoachIcon, labelKey: "nav.coach",    id: "coach",               isCustom: true  },
    { to: "/app/settings", icon: Settings,  labelKey: "nav.settings", id: "settings",            isCustom: false },
  ];

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-40 flex justify-center pointer-events-none"
      style={{
        // Bottom breathing room comes from two layers:
        //   1. `env(safe-area-inset-bottom)` — the physical inset on
        //      iPhones (home indicator zone) and modern Android
        //      3-button gesture bars. Zero on Android Chrome / most
        //      desktop browsers.
        //   2. A 14px floor via `max(...)` — guarantees the pill
        //      never touches the OS chrome on browsers that report
        //      inset=0 (typical Samsung/Chrome Android where the
        //      3-nav buttons still sit under our webview).
        paddingBottom: "max(14px, env(safe-area-inset-bottom, 0px))",
      }}
    >
      <div className="relative w-full max-w-md pointer-events-auto">
        <nav
          className={`mx-3 mb-0 rounded-3xl bg-white/95 backdrop-blur-2xl border border-neutral-100 shadow-card ${items.length > 5 ? "px-1" : "px-2"} py-2 flex items-center justify-around`}
          data-testid="bottom-nav"
        >
          {items.map((it) => (
            <NavLink
              key={it.id}
              to={it.to}
              end={it.end}
              // Kick the prefetch the instant the user touches the icon.
              // The router still handles the actual navigation — this only
              // ensures the JS is already in memory by the time it renders.
              onPointerDown={() => prefetch(it.id)}
              onTouchStart={() => prefetch(it.id)}
              onFocus={() => prefetch(it.id)}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 px-0.5 py-1.5 rounded-2xl transition-colors flex-1 min-w-0 max-w-[90px] ${
                  isActive ? "text-brand-500" : "text-neutral-400"
                }`
              }
              data-testid={`nav-${it.id}`}
            >
              {({ isActive }) => (
                <>
                  {it.isCustom ? (
                    <it.icon active={isActive} />
                  ) : (
                    <it.icon className="w-5 h-5" strokeWidth={isActive ? 2.4 : 2} />
                  )}
                  <span className={`${items.length > 5 ? "text-[9.5px] tracking-tight" : "text-[10px]"} font-semibold whitespace-nowrap overflow-hidden w-full text-center leading-tight`}>
                    {t(it.labelKey)}
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
